import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, BookOpen, Droplets, TrendingUp, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PrimaryActionCardProps {
  checkinDone: boolean;
  protocolDoneToday: boolean;
  waterToday: number;
}

const PrimaryActionCard = ({ checkinDone, protocolDoneToday, waterToday }: PrimaryActionCardProps) => {
  const navigate = useNavigate();

  // Determine the best next action
  const getAction = () => {
    if (!checkinDone) {
      return {
        icon: ClipboardCheck,
        title: 'Haz tu check-in diario',
        description: 'Registra cómo te sientes hoy para personalizar tu experiencia.',
        time: '~1 min',
        cta: 'Hacer check-in',
        path: '/app/checkin',
      };
    }
    if (!protocolDoneToday) {
      return {
        icon: BookOpen,
        title: 'Continúa tu protocolo',
        description: 'Tu próximo paso del protocolo de 21 días te está esperando.',
        time: '5–15 min',
        cta: 'Ver protocolo',
        path: '/app/protocolo',
      };
    }
    if (waterToday < 4) {
      return {
        icon: Droplets,
        title: 'Registra tu agua',
        description: 'Llevas pocos vasos hoy. La hidratación es clave para tu bienestar.',
        time: '~30 seg',
        cta: 'Registrar agua',
        path: '/app/checkin',
      };
    }
    return {
      icon: TrendingUp,
      title: 'Revisa tu progreso',
      description: '¡Excelente día! Mira cómo tus hábitos están dando resultados.',
      time: '',
      cta: 'Ver progreso',
      path: '/app/progresso',
    };
  };

  const action = getAction();
  const Icon = action.icon;

  return (
    <div className="gradient-primary rounded-2xl p-5 text-primary-foreground relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/10 rounded-full pointer-events-none" />
      <div className="absolute -top-2 -right-6 w-12 h-12 bg-white/5 rounded-full pointer-events-none" />

      <div className="relative z-10">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-heading font-semibold">{action.title}</h3>
            <p className="text-sm text-primary-foreground/80 mt-0.5 leading-relaxed">
              {action.description}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          {action.time && (
            <span className="text-xs text-primary-foreground/60 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {action.time}
            </span>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto bg-white/20 hover:bg-white/30 text-primary-foreground border-0 rounded-xl font-medium"
            onClick={() => navigate(action.path)}
          >
            {action.cta}
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrimaryActionCard;
